import type { Component, Style } from '@wix/thunderbolt-becky-types'
import { createCssCompNode } from '../cssCompNode'
import type { CompInfo, NativeExtensions } from '@wix/thunderbolt-components-native'
import { compInfoNodes } from '@wix/thunderbolt-component-isolation'
import { mapValues } from '../../utils/mapValues'
import type { NodeRefs } from '@wix/thunderbolt-catharsis'
import { createCompNode } from '@wix/thunderbolt-catharsis'
import _ from 'lodash'
import type { StructureOverrides } from '../modesCompNode'
import { withMapperModes } from '../modesCompNode'
import type { Css, VariantsCss } from '../../shared.types'
import { additionalStructure } from './compAdditionalStructure'
import type { RunAndReportFn } from '@wix/thunderbolt-state-manager'

const mapperDepsToQueryName: Partial<{ [key in keyof CompInfo]: StructureOverrides[0] }> = {
	compProps: 'propertyQuery',
	compLayout: 'layout',
	compData: 'dataQuery',
}

const CSS_MAPPER_NAME = 'compCssMappers'

const getAdditionalData = (component: Component, refs: NodeRefs<any>) => {
	if (
		!_.isEmpty(component.components) &&
		['RefComponent', 'Repeater', 'SelectableContainer'].includes(component.componentType)
	) {
		return { childData: refs.childDataCompNode(component.components![0]) }
	}
	return {}
}

export const childDataCompNode = createCompNode({
	getDependencies: (component, refs) => {
		return {
			hasResponsiveLayout: compInfoNodes.hasResponsiveLayout.getDependencies(component, refs as NodeRefs<any>),
			design: compInfoNodes.compDesign.getDependencies(component, refs as NodeRefs<any>),
			styleProperties: compInfoNodes.styleProperties.getDependencies(component, refs as NodeRefs<any>),
		}
	},
	toViewItem: (component, deps) => {
		return {
			childHasResponsiveLayout: compInfoNodes.hasResponsiveLayout.toViewItem(
				component,
				deps.hasResponsiveLayout as never
			),
			childDesign: compInfoNodes.compDesign.toViewItem(component, deps.design as never),
			childStyleProperties: compInfoNodes.styleProperties.toViewItem(component, deps.styleProperties as never),
		}
	},
})

export const compCssMappersWithModesAndVariants = (
	{ extensions }: NativeExtensions,
	name: any = CSS_MAPPER_NAME,
	runAndReport: RunAndReportFn
) => {
	const structureOverrides: { [componentType: string]: StructureOverrides } = mapValues(extensions, (extension) => {
		return Object.entries(extension.deps).reduce<StructureOverrides>((acc, [dep]) => {
			// @ts-ignore
			if (dep && mapperDepsToQueryName[dep]) {
				// @ts-ignore
				acc.push(mapperDepsToQueryName[dep])
			}
			return acc
		}, [])
	})

	const getDependencies = (component: Component, refs: NodeRefs<any>, originalComponent?: Component) => {
		const extension = extensions[component.componentType]
		if (!extension) {
			return null
		}

		const mapperDeps = mapValues(extension.deps, (__, dep) => {
			if (dep === 'styleProperties') {
				return refs.compStyles(originalComponent || component)
			} else if (dep === 'uiType') {
				return refs.uiType(component)
			}

			if (!compInfoNodes[dep]) {
				console.warn(`compCssMappers: missing compInfoNode for ${dep}`)
			}

			return compInfoNodes[dep].getDependencies(component, refs as NodeRefs<any>)
		})

		const additionalData = getAdditionalData(component, refs as NodeRefs<any>)

		return {
			mapperDeps,
			additionalData,
		}
	}

	const toViewItem = (component: Component, deps: any) => {
		const extension = extensions[component.componentType]
		if (!deps || !extension) {
			return null
		}

		const compInfo = mapValues(extension.deps, (__, dep) => {
			if (dep === 'styleProperties' || dep === 'uiType') {
				return deps.mapperDeps[dep]
			}

			return compInfoNodes[dep].toViewItem(component, deps.mapperDeps[dep] as never)
		}) as CompInfo

		const styleProperties = (compInfo.styleProperties as any) as VariantsCss<Style>
		if (styleProperties) {
			return {
				type: 'variants' as const,
				css: mapValues(styleProperties.css, (variantsToStyle, breakpointId) =>
					mapValues(variantsToStyle, (style: Style, variantKey) =>
						extension.resolver(
							{
								...compInfo,
								styleProperties: style?.spx
									? { ...style.style?.properties, spx: style.spx }
									: style.style?.properties,
								isDefaultVariant: breakpointId === 'default' && !variantKey.trim().length,
								runAndReport,
							} as any,
							deps.additionalData as any
						)
					)
				),
			}
		}

		return {
			type: 'classic' as const,
			css: {
				'': extension.resolver(compInfo, deps.additionalData as any),
			},
		}
	}

	const _variantsCssMapper = createCompNode({
		getDependencies: (component: Component, refs) => {
			const compDependencies = getDependencies(component, refs as NodeRefs<any>)

			const compType = component.componentType
			if (compDependencies && additionalStructure[compType]) {
				return {
					[compType]: compDependencies,
					...mapValues(additionalStructure[compType], ({ get }) =>
						getDependencies(get(component), refs as NodeRefs<any>, component)
					),
				}
			}

			return compDependencies
		},
		toViewItem: (component, deps) => {
			const compType = component.componentType
			if (deps && additionalStructure[compType]) {
				const dependencies = deps as Record<string, any>
				return {
					...toViewItem(component, dependencies[compType])!,
					additionalCss: _.reduce(
						additionalStructure[compType],
						(acc, { get, repeated = true }, compName) => {
							const comp = get(component)
							acc[comp.id] = {
								css: toViewItem(comp, dependencies[compName])! as Css<Style>,
								repeated,
							}

							return acc
						},
						{} as Record<string, { css: Css<Style>; repeated: boolean }>
					),
				}
			}

			return toViewItem(component, deps)
		},
	})
	const _modesCssMapper = createCompNode({
		getDependencies: (component: Component, refs: NodeRefs<any>) => {
			const extension = extensions[component.componentType]
			if (!extension) {
				return null
			}
			const mapperDeps = mapValues(extension.deps, (__, dep) => {
				if (dep === 'uiType') {
					return refs.uiType(component)
				}

				if (!compInfoNodes[dep]) {
					console.warn(`compCssMappers: missing compInfoNode for ${dep}`)
				}

				return compInfoNodes[dep].getDependencies(component, refs as NodeRefs<any>)
			})
			return mapperDeps
		},
		toViewItem: (component, deps) => {
			const extension = extensions[component.componentType]
			if (!deps || !extension) {
				return null
			}

			const compInfo = mapValues(extension.deps, (__, dep) => {
				if (dep === 'uiType') {
					return deps[dep]
				}

				return compInfoNodes[dep].toViewItem(component, deps[dep] as never)
			}) as CompInfo

			return extension.resolver(
				{
					...compInfo,
					// @ts-expect-error - Remove once isDefaultVariant is added to the compInfo
					...(compInfo.isDefaultVariant && {
						isDefaultVariant: true,
					}),
				},
				deps.additionalData as any
			)
		},
	})

	return createCssCompNode(
		'compCssMappers',
		name,
		withMapperModes(_variantsCssMapper, _modesCssMapper, ({ componentType, modes }) =>
			modes ? structureOverrides[componentType] || [] : []
		)
	)
}
